import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { 
  TrendingUp, 
  Users, 
  Brain, 
  Target, 
  BookOpen,
  ChevronRight,
  Award,
  Clock,
  BarChart3,
  Settings,
  Zap
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface LearningPath {
  id: string;
  name: string;
  description: string;
  totalSteps: number;
  estimatedDuration: string;
  difficulty: "beginner" | "intermediate" | "advanced";
  specialty: string;
  prerequisites: string[];
  learningObjectives: string[];
  adaptiveFeatures: string[];
  enrolledUsers: number;
  completionRate: number;
  avgRating: number;
}

interface PathRecommendation {
  id: string;
  userId: string;
  userName: string;
  currentPath?: string;
  recommendedPaths: {
    pathId: string;
    pathName: string;
    reason: string;
    confidence: number;
    estimatedSuccess: number;
  }[];
  weakAreas: string[];
  strongAreas: string[];
  learningStyle: string;
  progressMetrics: {
    averageScore: number;
    completionRate: number;
    studyTime: string;
  };
}

export default function AdaptiveLearningPaths() {
  const { toast } = useToast();
  const [selectedSpecialty, setSelectedSpecialty] = useState("all");
  const [adaptiveMode, setAdaptiveMode] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  const [learningPaths, setLearningPaths] = useState<LearningPath[]>([
    {
      id: "path-1",
      name: "Cardiovascular Medicine Mastery",
      description: "Comprehensive pathway covering heart anatomy, physiology, pathology, and treatment",
      totalSteps: 45,
      estimatedDuration: "6-8 weeks",
      difficulty: "intermediate",
      specialty: "cardiology",
      prerequisites: ["Basic anatomy", "Medical terminology"],
      learningObjectives: [
        "Understand cardiac anatomy and physiology",
        "Diagnose common cardiovascular conditions",
        "Apply appropriate treatment protocols",
        "Interpret ECG and cardiac imaging"
      ],
      adaptiveFeatures: [
        "Difficulty adjustment based on performance",
        "Personalized review schedules",
        "Prerequisite gap filling",
        "Progress-based content recommendations"
      ],
      enrolledUsers: 234,
      completionRate: 78,
      avgRating: 4.7
    },
    {
      id: "path-2", 
      name: "Pharmacology Fundamentals",
      description: "Essential drug knowledge for medical professionals",
      totalSteps: 32,
      estimatedDuration: "4-5 weeks",
      difficulty: "beginner",
      specialty: "pharmacology",
      prerequisites: ["Basic chemistry", "Human physiology"],
      learningObjectives: [
        "Understand drug mechanisms of action",
        "Calculate appropriate dosages", 
        "Identify drug interactions",
        "Apply pharmacokinetic principles"
      ],
      adaptiveFeatures: [
        "Dosage calculation practice",
        "Drug interaction warnings",
        "Case-based scenarios",
        "Spaced repetition for drug names"
      ],
      enrolledUsers: 456,
      completionRate: 85,
      avgRating: 4.5
    },
    {
      id: "path-3",
      name: "Advanced Surgical Procedures",
      description: "Complex surgical techniques and decision-making",
      totalSteps: 67,
      estimatedDuration: "10-12 weeks",
      difficulty: "advanced",
      specialty: "surgery",
      prerequisites: ["Basic surgical skills", "Anatomy mastery", "Pathology knowledge"],
      learningObjectives: [
        "Master advanced surgical techniques",
        "Develop surgical decision-making skills",
        "Understand complication management",
        "Apply minimally invasive approaches"
      ],
      adaptiveFeatures: [
        "Simulation-based learning",
        "Case complexity progression",
        "Peer learning recommendations",
        "Expert mentor matching"
      ],
      enrolledUsers: 89,
      completionRate: 62,
      avgRating: 4.9
    }
  ]);

  const [pathRecommendations, setPathRecommendations] = useState<PathRecommendation[]>([
    {
      id: "rec-1",
      userId: "user-1",
      userName: "Dr. Sarah Chen",
      currentPath: "Cardiovascular Medicine Mastery",
      recommendedPaths: [
        {
          pathId: "path-4",
          pathName: "Advanced Cardiac Imaging",
          reason: "Strong performance in cardiovascular basics, ready for imaging specialization",
          confidence: 92,
          estimatedSuccess: 87
        },
        {
          pathId: "path-5", 
          pathName: "Interventional Cardiology",
          reason: "High scores in treatment protocols, good candidate for procedures",
          confidence: 85,
          estimatedSuccess: 82
        }
      ],
      weakAreas: ["ECG interpretation", "Cardiac catheterization"],
      strongAreas: ["Anatomy", "Pharmacology", "Basic diagnostics"],
      learningStyle: "Visual + Hands-on",
      progressMetrics: {
        averageScore: 84,
        completionRate: 73,
        studyTime: "4.2 hrs/week"
      }
    },
    {
      id: "rec-2",
      userId: "user-2", 
      userName: "Dr. Michael Rodriguez",
      recommendedPaths: [
        {
          pathId: "path-1",
          pathName: "Cardiovascular Medicine Mastery",
          reason: "Strong anatomy foundation, struggling with physiology - this path provides good bridge",
          confidence: 78,
          estimatedSuccess: 76
        },
        {
          pathId: "path-2",
          pathName: "Pharmacology Fundamentals", 
          reason: "Medication knowledge gaps identified in recent assessments",
          confidence: 88,
          estimatedSuccess: 85
        }
      ],
      weakAreas: ["Physiology", "Drug interactions", "Complex calculations"],
      strongAreas: ["Anatomy", "Medical terminology", "Patient communication"],
      learningStyle: "Reading + Practice",
      progressMetrics: {
        averageScore: 67,
        completionRate: 45,
        studyTime: "2.8 hrs/week"
      }
    }
  ]);

  const generateAdaptivePaths = async () => {
    setIsGenerating(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('ai-adaptive-paths', {
        body: {
          specialty: selectedSpecialty,
          includeUserData: true,
          adaptiveMode: adaptiveMode
        }
      });

      if (error) throw error;
      
      toast({
        title: "Paths Generated",
        description: `Generated ${data.newPaths} adaptive learning paths.`,
      });
      
    } catch (error) {
      console.error('Error generating adaptive paths:', error);
      toast({
        title: "Generation Failed",
        description: "Failed to generate adaptive paths. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const enrollInPath = (pathId: string) => {
    const path = learningPaths.find(p => p.id === pathId);
    toast({
      title: "Enrollment Started",
      description: `User enrolled in: ${path?.name}`,
    });
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "beginner": return "text-success";
      case "intermediate": return "text-warning";
      case "advanced": return "text-destructive";
      default: return "text-muted-foreground";
    }
  };

  const getCompletionColor = (rate: number) => {
    if (rate >= 80) return "text-success";
    if (rate >= 60) return "text-warning";
    return "text-destructive";
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return "text-success";
    if (confidence >= 75) return "text-primary"; 
    if (confidence >= 60) return "text-warning";
    return "text-destructive";
  };

  const filteredPaths = learningPaths.filter(path => 
    selectedSpecialty === "all" || path.specialty === selectedSpecialty
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Adaptive Learning Paths</h3>
          <p className="text-sm text-muted-foreground">
            Personalized learning journeys that adapt to individual progress and performance
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Adaptive Mode</label>
            <Switch checked={adaptiveMode} onCheckedChange={setAdaptiveMode} />
          </div>
          <Button onClick={generateAdaptivePaths} disabled={isGenerating} className="flex items-center gap-2">
            <Brain className={`h-4 w-4 ${isGenerating ? 'animate-spin' : ''}`} />
            {isGenerating ? "Generating..." : "Generate Paths"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Active Paths
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{learningPaths.length}</div>
            <p className="text-xs text-muted-foreground">Available learning paths</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4" />
              Total Enrollments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {learningPaths.reduce((acc, path) => acc + path.enrolledUsers, 0)}
            </div>
            <p className="text-xs text-muted-foreground">Learners enrolled</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Target className="h-4 w-4" />
              Avg Completion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">
              {Math.round(learningPaths.reduce((acc, path) => acc + path.completionRate, 0) / learningPaths.length)}%
            </div>
            <p className="text-xs text-muted-foreground">Success rate</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Award className="h-4 w-4" />
              Avg Rating
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">
              {(learningPaths.reduce((acc, path) => acc + path.avgRating, 0) / learningPaths.length).toFixed(1)}
            </div>
            <p className="text-xs text-muted-foreground">Out of 5 stars</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Learning Paths
                </CardTitle>
                <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Specialties</SelectItem>
                    <SelectItem value="cardiology">Cardiology</SelectItem>
                    <SelectItem value="pharmacology">Pharmacology</SelectItem>
                    <SelectItem value="surgery">Surgery</SelectItem>
                    <SelectItem value="pediatrics">Pediatrics</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {filteredPaths.map((path) => (
                <Card key={path.id} className="border">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium">{path.name}</h4>
                          <Badge variant="outline" className={getDifficultyColor(path.difficulty)}>
                            {path.difficulty}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {path.specialty}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          {path.description}
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                          <div>
                            <div className="font-medium">{path.totalSteps}</div>
                            <div className="text-muted-foreground">Steps</div>
                          </div>
                          <div>
                            <div className="font-medium">{path.estimatedDuration}</div>
                            <div className="text-muted-foreground">Duration</div>
                          </div>
                          <div>
                            <div className="font-medium">{path.enrolledUsers}</div>
                            <div className="text-muted-foreground">Enrolled</div>
                          </div>
                          <div>
                            <div className={`font-medium ${getCompletionColor(path.completionRate)}`}>
                              {path.completionRate}%
                            </div>
                            <div className="text-muted-foreground">Completion</div>
                          </div>
                        </div>
                      </div>
                      <Button size="sm" onClick={() => enrollInPath(path.id)}>
                        View Details
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-3">
                      <div>
                        <h5 className="text-xs font-medium mb-1">Prerequisites:</h5>
                        <div className="flex flex-wrap gap-1">
                          {path.prerequisites.map((prereq, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {prereq}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h5 className="text-xs font-medium mb-1">Adaptive Features:</h5>
                        <ul className="space-y-1">
                          {path.adaptiveFeatures.slice(0, 2).map((feature, index) => (
                            <li key={index} className="text-xs text-muted-foreground flex items-center gap-2">
                              <Zap className="h-3 w-3 text-primary" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-4 w-4" />
                Personalized Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {pathRecommendations.map((rec) => (
                <div key={rec.id} className="p-3 border rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h5 className="font-medium text-sm">{rec.userName}</h5>
                      {rec.currentPath && (
                        <p className="text-xs text-muted-foreground">
                          Current: {rec.currentPath}
                        </p>
                      )}
                    </div>
                    <div className="text-right text-xs">
                      <div className="font-medium">{rec.progressMetrics.averageScore}%</div>
                      <div className="text-muted-foreground">Avg Score</div>
                    </div>
                  </div>

                  <Separator className="my-2" />

                  <div className="space-y-2">
                    {rec.recommendedPaths.slice(0, 2).map((path, index) => (
                      <div key={index} className="p-2 bg-muted/50 rounded">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium">{path.pathName}</span>
                          <span className={`text-xs ${getConfidenceColor(path.confidence)}`}>
                            {path.confidence}%
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {path.reason}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-muted-foreground">Study Time</div>
                      <div className="font-medium">{rec.progressMetrics.studyTime}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Completion</div>
                      <div className="font-medium">{rec.progressMetrics.completionRate}%</div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Adaptive Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">Auto-Difficulty Adjustment</div>
                    <div className="text-xs text-muted-foreground">
                      Automatically adjust based on performance
                    </div>
                  </div>
                  <Switch defaultChecked />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">Prerequisite Detection</div>
                    <div className="text-xs text-muted-foreground">
                      Identify and fill knowledge gaps
                    </div>
                  </div>
                  <Switch defaultChecked />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">Personalized Scheduling</div>
                    <div className="text-xs text-muted-foreground">
                      Optimize study timing for retention
                    </div>
                  </div>
                  <Switch />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">Peer Matching</div>
                    <div className="text-xs text-muted-foreground">
                      Connect learners with similar goals
                    </div>
                  </div>
                  <Switch />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}